/**
 * Frontend action-vocabulary + capability model (Automations cross-object actions,
 * design 2026-06-30 §5.3 / §7).
 *
 * The SERVER's source of truth is `@xavior/agent-policy` (`ACTION_REGISTRY` /
 * `ACTION_VOCABULARY`). apps/web MAY NOT import that package — the web package boundary
 * only permits `@xavior/{contracts,schema,shared,task-import-export}` (see
 * apps/web/AGENTS.md → Monorepo Package Boundaries). So this module is the hand-maintained
 * FRONTEND MIRROR of the two facts the UI needs:
 *
 *   1. Which action types are `consequential` — they render a "sign-off" reversibility
 *      badge, never "free". This replaces the ad-hoc `SIGN_OFF_ACTIONS` set in
 *      `liveAutomationsClient.ts` that drifted out of lockstep with the API registry (it
 *      was missing the ~13 newly-consequential cross-object types, so `note.create` /
 *      `task.create` / `event.*` / `contact.*` wrongly rendered as "free").
 *   2. The coarse capability GROUPS a user can opt into. Each maps to the concrete action
 *      types written into an automation's `actionAllowlist` (the config PATCH validates
 *      the result against the server `ACTION_VOCABULARY`, D7). Every capability is OFF by
 *      default — an empty allowlist enables nothing.
 *
 * Keep this in lockstep with `packages/agent-policy/src/index.ts`. When a type becomes
 * consequential on the server, add it here so its badge is correct.
 */

/**
 * Every `consequential` action type — the ones that route to human sign-off. Mirror of the
 * `consequential` entries in the server `ACTION_REGISTRY` (both the apps/api dot vocabulary
 * and the apps/x-agent snake_case tool spellings). Anything NOT in this set renders a
 * "free" reversibility badge (display-only; the real fail-closed classification lives
 * server-side in `classifyAction`, which defaults unknown types to `consequential`).
 */
export const CONSEQUENTIAL_ACTION_TYPES: ReadonlySet<string> = new Set<string>([
  // apps/api dot vocabulary
  'email.send',
  'message.send',
  'notification.send',
  'event.create_with_attendees',
  'event.move_with_attendees',
  'object.update',
  'object.delete',
  'sharing.modify',
  // apps/api email-sourced cross-object actions (the ~13 that drifted)
  'event.create',
  'event.update',
  'event.delete',
  'task.create',
  'task.update',
  'task.delete',
  'note.create',
  'note.update',
  'note.delete',
  'contact.create',
  'contact.update',
  'contact.delete',
  // apps/x-agent snake_case tool vocabulary
  'send_email',
  'delete_object',
  'restore_object',
  'publish_report',
]);

/** True when an action routes to human sign-off (renders the "sign-off" badge). */
export function isConsequentialAction(actionType: string): boolean {
  return CONSEQUENTIAL_ACTION_TYPES.has(actionType);
}

/** A coarse, user-opt-in capability group. Toggling one on/off writes/removes its concrete
 *  `actionTypes` from the automation's `actionAllowlist`. */
export interface CapabilityGroup {
  id: 'reply' | 'calendar' | 'tasks' | 'notes' | 'contacts';
  label: string;
  description: string;
  /** The concrete `actionType`s written into `actionAllowlist` when this capability is ON. */
  actionTypes: readonly string[];
}

/**
 * The capability groups shown as opt-in toggles. Mapping is explicit per the plan:
 *   reply → email.send, calendar → event.*, tasks → task.*, notes → note.*, contacts → contact.*
 *
 * `notification.send` is deliberately NOT a group: it is a platform preset capability (carried
 * by the daily-briefing / overnight / meeting-prep presets) and is `consequential` for badge
 * purposes, but it is not a cross-object capability, so it gets no toggle.
 */
export const CAPABILITY_GROUPS: readonly CapabilityGroup[] = [
  {
    id: 'reply',
    label: 'Draft replies',
    description: 'Draft an email reply for you to review before it sends.',
    actionTypes: ['email.send'],
  },
  {
    id: 'calendar',
    label: 'Manage calendar',
    description: 'Propose creating, editing, or removing calendar events.',
    actionTypes: ['event.create', 'event.update', 'event.delete'],
  },
  {
    id: 'tasks',
    label: 'Manage tasks',
    description: 'Propose creating, editing, or removing tasks.',
    actionTypes: ['task.create', 'task.update', 'task.delete'],
  },
  {
    id: 'notes',
    label: 'Manage notes',
    description: 'Propose creating, editing, or removing notes.',
    actionTypes: ['note.create', 'note.update', 'note.delete'],
  },
  {
    id: 'contacts',
    label: 'Manage contacts',
    description: 'Propose creating, editing, or removing contacts.',
    actionTypes: ['contact.create', 'contact.update', 'contact.delete'],
  },
];

/** A capability is ON only when EVERY one of its action types is in the allowlist — toggles
 *  write all-or-nothing, so a partial allowlist reads as OFF (and defaults OFF for `[]`). */
export function isCapabilityEnabled(
  group: CapabilityGroup,
  allowlist: readonly string[],
): boolean {
  const set = new Set(allowlist);
  return group.actionTypes.length > 0 && group.actionTypes.every((t) => set.has(t));
}

/** The set of enabled capability ids for an allowlist. Empty for a new automation (`[]`). */
export function enabledCapabilities(
  allowlist: readonly string[],
): Set<CapabilityGroup['id']> {
  return new Set(
    CAPABILITY_GROUPS.filter((g) => isCapabilityEnabled(g, allowlist)).map((g) => g.id),
  );
}

/** Return a NEW allowlist with `group`'s action types added (on) or removed (off). Deduped;
 *  other entries (e.g. `notification.send`, another capability's types) are preserved, so a
 *  toggle round-trips: `applyCapability(applyCapability(x, g, true), g, false)` restores `x`
 *  (as a set). */
export function applyCapability(
  allowlist: readonly string[],
  group: CapabilityGroup,
  on: boolean,
): string[] {
  const set = new Set(allowlist);
  for (const t of group.actionTypes) {
    if (on) set.add(t);
    else set.delete(t);
  }
  return [...set];
}

/**
 * Actions the inbox responder can never operate without. `email.send` is the responder's CORE
 * function: the "Draft replies" capability (`reply` group) is implicit and always-on for it, so
 * its toggle is HIDDEN from the inbox-watcher UI (see ActionCapabilityToggles `hiddenGroupIds`).
 * The capability-save path unions these in so a list built from the reduced UI can never clear
 * `email.send` from the effective allowlist. Server validation already accepts `email.send`
 * (it is in ACTION_VOCABULARY); this only guarantees the client never OMITS it.
 */
export const RESPONDER_CORE_ACTIONS: readonly string[] = ['email.send'];

/**
 * Return a NEW allowlist that always contains the responder's core actions (deduped), preserving
 * every other entry. Idempotent: applying it twice equals applying it once (as a set).
 */
export function withResponderCoreActions(allowlist: readonly string[]): string[] {
  const set = new Set(allowlist);
  for (const t of RESPONDER_CORE_ACTIONS) set.add(t);
  return [...set];
}

/** Coarse operation an action performs, for card rendering. */
export type ActionOperation = 'send' | 'create' | 'update' | 'delete' | 'other';

/** Classify an action type into a coarse operation. ONLY the email replies (`email.send` /
 *  `send_email`) are 'send' — the card's send branch renders an email To/Subject/Body layout
 *  labeled "Drafted reply" with a Send button, so a non-email send classified there would be a
 *  wrong-consent surface (`message.send`/`notification.send` carry no email fields). Everything
 *  unrecognized — including those and the run-worker snake_case types (`delete_object`, …) —
 *  falls to 'other', which the card FAILS CLOSED (approve disabled, reject available). */
export function actionOperation(actionType: string): ActionOperation {
  if (actionType === 'email.send' || actionType === 'send_email') return 'send';
  if (actionType.endsWith('.create')) return 'create';
  if (actionType.endsWith('.update')) return 'update';
  if (actionType.endsWith('.delete')) return 'delete';
  return 'other';
}

const OBJECT_TYPES = new Set(['event', 'task', 'note', 'contact']);

/** The object type an action concerns (`event` | `task` | `note` | `contact`), or null when
 *  the action isn't one of the cross-object types (e.g. `email.send`, `object.update`). */
export function actionObjectType(actionType: string): string | null {
  const base = actionType.split('.')[0];
  return OBJECT_TYPES.has(base) ? base : null;
}
